// The Study landing screen (plan Item B4): learning content first, tools last.
// Four panels — Openings (guided courses, B3), Collections (curated vendored
// PGN sets, D2/B2), My studies (this browser's local library), and Import
// (paste / upload / clipboard). Everything here is client-side + localStorage;
// the only server touch is fetching the static collection assets.

import { esc, h } from "../dom.ts";
import { getLang, t, tp } from "../i18n/index.ts";
import { navigate } from "../router.ts";
import { topNav } from "../ui/topnav.ts";
import { buildReview } from "../game/review.ts";
import { buildPgn, pgnFilename, pgnResultLine } from "../game/pgn.ts";
import { parseGames } from "../game/pgnImport.ts";
import {
  deleteStudy,
  getStudy,
  listStudies,
  MAX_STUDIES,
  saveStudies,
  type StudyGame,
} from "../game/studyStore.ts";
import { downloadText } from "../ui/clipboard.ts";
import { renderCollections } from "../game/collections.ts";
import {
  courseLeafPaths,
  courseMainline,
  OPENING_COURSES,
  type CourseGroup,
  type OpeningCourse,
} from "../data/openingCourses.ts";
import { courseCoverage } from "../game/openingProgress.ts";
import type { Color } from "../game/types.ts";
import { collapsibleSection } from "../ui/collapsible.ts";

export function studyScreen(): HTMLElement {
  // Study lives in this browser: guests and members alike, no member login.

  const el = h(`
    <main class="card wide study">
      <section class="panel study-tools"><h1>${t("nav.study")}</h1>
        <a class="btn primary" data-link href="/analysis">${t("analysis.board")}</a>
        <a class="btn ghost" data-link href="/play/computer">${t("computer.title")}</a>
      </section>
      <section class="panel">
        <div class="panel-head"><h2>${t("openings.title")}</h2></div>
        <p class="fine">${t("openings.panelHelp")}</p>
        <p class="fine local-progress-note">${t("openings.localProgress")}</p>
        <div class="collapsible-sections"></div>
      </section>

      <section class="panel">
        <div class="panel-head"><h2>${t("study.collections")}</h2></div>
        <div class="collapsible-sections collections"></div>
      </section>

      <section class="panel">
        <div class="panel-head"><h2>${t("study.myStudies")}</h2></div>
        <div class="studies"></div>
      </section>

      <section class="panel">
        <h2>${t("study.importTitle")}</h2>
        <p class="fine">${t("study.importHelp")}</p>
        <textarea class="pgn-input" rows="6" spellcheck="false"
          placeholder="[Event &quot;…&quot;]\n1. e4 e5 2. Nf3 …"></textarea>
        <div class="import-actions">
          <button class="btn primary import-btn" type="button">${t("study.import")}</button>
          <label class="btn ghost file-label">${t("study.chooseFile")}<input class="file-input" type="file" accept=".pgn,.txt" hidden></label>
          <button class="btn ghost clip-btn" type="button" hidden>${t("study.fromClipboard")}</button>
        </div>
        <p class="import-msg msg" role="status"></p>
      </section>
    </main>
  `);
  el.prepend(topNav("study"));

  const textarea = el.querySelector<HTMLTextAreaElement>(".pgn-input")!;
  const msg = el.querySelector<HTMLElement>(".import-msg")!;
  const studiesBox = el.querySelector<HTMLElement>(".studies")!;

  const setMsg = (text: string, kind: "ok" | "bad" | "" = "") => {
    msg.textContent = text;
    msg.className = "import-msg msg" + (kind ? ` ${kind}` : "");
  };

  const doImport = (text: string, source: StudyGame["source"]): void => {
    const trimmed = text.trim();
    if (!trimmed) {
      setMsg(t("study.nothingToImport"), "bad");
      return;
    }
    const { games, total } = parseGames(trimmed);
    if (games.length === 0) {
      setMsg(t("study.noGamesFound"), "bad");
      return;
    }
    // One batched write, so a >MAX import can't evict its own earliest games.
    const result = saveStudies(
      games.map((g) => ({ moves: g.moves, initialFen: g.initialFen, meta: g.meta, source })),
    );
    renderStudies();

    if (result.error) {
      setMsg(t("study.saveFailed"), "bad");
      return;
    }

    const skipped = total - games.length; // couldn't be parsed
    const saved = result.persistedIds.length;
    const droppedByCap = games.length - saved;
    const base = tp("study.imported", saved);
    const notes: string[] = [];
    if (skipped > 0) notes.push(t("study.skipped", { n: skipped }));
    if (droppedByCap > 0) notes.push(t("study.droppedByCap", { n: droppedByCap, max: MAX_STUDIES }));
    setMsg(notes.length ? `${base} ${notes.join("; ")}.` : base, "ok");

    // Exactly one game → open it straight away.
    const lastId = result.persistedIds[result.persistedIds.length - 1];
    if (games.length === 1 && lastId) navigate(`/study/game/${lastId}`);
  };

  el.querySelector<HTMLButtonElement>(".import-btn")!.addEventListener("click", () =>
    doImport(textarea.value, "import"),
  );

  el.querySelector<HTMLInputElement>(".file-input")!.addEventListener("change", (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      textarea.value = text;
      doImport(text, "file");
    };
    reader.onerror = () => setMsg(t("study.readFailed"), "bad");
    reader.readAsText(file);
  });

  // "From clipboard" only when the async Clipboard read API is available.
  const clipBtn = el.querySelector<HTMLButtonElement>(".clip-btn")!;
  if (navigator.clipboard?.readText) {
    clipBtn.hidden = false;
    clipBtn.addEventListener("click", async () => {
      try {
        const text = await navigator.clipboard.readText();
        textarea.value = text;
        doImport(text, "clipboard");
      } catch {
        setMsg(t("study.clipboardFailed"), "bad");
      }
    });
  }

  // Row actions (delete / download) are caught here so the row's own link doesn't
  // also navigate (the router's document handler bails on defaultPrevented).
  studiesBox.addEventListener("click", (e) => {
    const del = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-del]");
    if (del) {
      e.preventDefault();
      e.stopPropagation();
      deleteStudy(del.dataset.del!);
      renderStudies();
      return;
    }
    const dl = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-dl-study]");
    if (dl) {
      e.preventDefault();
      e.stopPropagation();
      downloadStudy(dl.dataset.dlStudy!);
    }
  });

  function renderStudies(): void {
    const all = listStudies();
    if (all.length === 0) {
      studiesBox.innerHTML = `<p class="fine">${t("study.emptyStudies")}</p>`;
      return;
    }
    studiesBox.replaceChildren();
    for (const g of all) studiesBox.append(studyRow(g));
  }

  renderStudies();
  renderOpeningCourses(el.querySelector<HTMLElement>(".collapsible-sections")!);
  void renderCollections(el.querySelector<HTMLElement>(".collections")!);
  return el;
}

/** Courses nested into native, keyboard-accessible collapsible sections.
 *  Sections follow the classical first-move families — the header already
 *  tells the learner how every opening inside begins. */
function renderOpeningCourses(box: HTMLElement): void {
  const lang = getLang();
  const sections: { group: CourseGroup; label: string; help: string }[] = [
    { group: "open", label: t("openings.openGames"), help: t("openings.openGamesHelp") },
    { group: "semi-open", label: t("openings.semiOpenGames"), help: t("openings.semiOpenGamesHelp") },
    { group: "closed-flank", label: t("openings.closedFlank"), help: t("openings.closedFlankHelp") },
  ];
  for (const section of sections) {
    const courses = OPENING_COURSES.filter((course) => course.group === section.group);
    const details = collapsibleSection(`opening:${section.group}`, section.label, section.help, courses.length);
    const list = details.querySelector<HTMLElement>(".section-body")!;
    for (const course of courses) list.append(openingCourseCard(course, lang));
    box.append(details);
  }
}

function openingCourseCard(course: OpeningCourse, lang: "en" | "sl"): HTMLElement {
  const total = courseLeafPaths(course).length;
  const kind = course.kind ?? "guided";
  const path = `/study/openings/${encodeURIComponent(course.slug)}`;
  const coverage = (side: Color): string => {
    const progress = courseCoverage(course.slug, side, courseLeafPaths(course));
    if (!progress.started) return "";
    return ` · ${progress.complete ? "✓ " : ""}${progress.covered}/${total}`;
  };
  // The default training side is highlighted so the card link's destination
  // is visible before clicking; the other side stays one tap away.
  const sideLink = (side: Color): string => `
    <a class="btn ghost sm${side === course.color ? " active" : ""}" data-link
       href="${path}/${side}">${t(`chess.${side}`)}${coverage(side)}</a>`;
  const card = document.createElement("article");
  card.className = `game-row course-card ${kind}`;
  card.innerHTML = `
    <a class="course-card-main" data-link href="${path}/${course.color}">
      <span class="opp">${esc(course.name[lang])}</span>
      <span class="meta">${esc(`${course.eco} · ${t(`openings.tier.${kind}`)} · ${course.tagline[lang]}`)}</span>
      <span class="line-preview">${esc(courseMainline(course))}</span>
    </a>
    <span class="course-side-links" aria-label="${t("openings.chooseSide")}">
      ${sideLink("white")}${sideLink("black")}
    </span>`;
  return card;
}

/** One row in the My-studies list. */
function studyRow(g: StudyGame): HTMLElement {
  const a = document.createElement("a");
  a.className = "game-row study-row";
  a.setAttribute("data-link", "");
  a.href = `/study/game/${encodeURIComponent(g.id)}`;
  const plies = g.moves.length;
  const movesLabel = tp("plural.moves", Math.ceil(plies / 2));
  const parts = [pgnResultLine(g.meta), movesLabel];
  if (g.meta.date && /^\d/.test(g.meta.date)) parts.push(g.meta.date);
  if (g.meta.opening) parts.push(g.meta.opening);
  a.innerHTML = `
    <span class="opp">${esc(g.meta.white)} vs ${esc(g.meta.black)}</span>
    <span class="meta">${esc(parts.join(" · "))}</span>
    <span class="row-btns">
      <button class="row-dl" type="button" data-dl-study="${esc(g.id)}" title="${t("history.downloadPgn")}" aria-label="${t("history.downloadPgn")}">⬇</button>
      <button class="row-del" type="button" data-del="${esc(g.id)}" title="${t("study.delete")}" aria-label="${t("study.delete")}">✕</button>
    </span>`;
  return a;
}

/** Download one stored study as a `.pgn`. */
function downloadStudy(id: string): void {
  const g = getStudy(id);
  if (!g) return;
  const review = buildReview(g.moves, g.initialFen);
  downloadText(pgnFilename(g.meta), buildPgn(review, g.meta));
}
